import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarDays, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Seo } from "@/components/Seo";
import { ReviewsWall } from "@/components/ReviewsWall";
import { FadeUp, CountUp, SectionHeading } from "@/components/anim";
import { api } from "@/lib/api";
import { STYLE_META, TERM, VENUES, daysUntilTerm, fmtTime, fmtDate, FALLBACK_IMAGE } from "@/lib/site";

const TermCountdown = () => {
  const days = daysUntilTerm();
  if (days < 0) return null; // term has started — nothing to count down to
  const label =
    days === 0 ? "Term starts today!" : days === 1 ? "Term starts tomorrow!" : `Term starts in ${days} days`;
  return (
    <div
      className="mt-6 inline-flex items-center gap-3 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] grain px-4 py-2.5 -rotate-1"
      data-testid="home-term-countdown"
    >
      <CalendarDays className="h-4 w-4 shrink-0" />
      <span className="font-display uppercase text-2xl leading-none tracking-[0.02em]">{label}</span>
    </div>
  );
};

const HERO_IMG = "https://images.unsplash.com/photo-1629219222026-e48dcc036c3f?w=1100&q=85";
const SEASIDE_IMG = "https://images.unsplash.com/photo-1577997146693-a20148a112f3?w=1200&q=80";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const Hero = () => (
  <section className="grain border-b-[0.5px] border-foreground/30 overflow-hidden" data-testid="home-hero-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
      <div className="lg:col-span-7 flex flex-col justify-center">
        <FadeUp>
          <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground">
            Kerry&rsquo;s &middot; formerly Newcomb Theatre Arts &middot; Whitstable, Kent
          </p>
        </FadeUp>
        <FadeUp delay={0.06}>
          <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.8rem] leading-[0.88] mt-6 text-foreground">
            Every kid gets
            <br />
            <span className="text-[hsl(var(--kerrys-coral))]">their moment.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.12}>
          <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            We&rsquo;re Kerry&rsquo;s &mdash; Whitstable&rsquo;s dance school for ages 2 to 18. ISTD ballet, tap and
            modern, plus musical theatre, street, contemporary, singing and audition preparation. Great music, kind
            teaching, proper technique.
          </p>
        </FadeUp>
        <FadeUp delay={0.18}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              asChild
              size="lg"
              data-testid="home-hero-book-button"
              className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
            >
              <Link to="/book">
                Book a class <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="home-hero-timetable-button"
              className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
            >
              <Link to="/timetable">See the timetable</Link>
            </Button>
          </div>
          <TermCountdown />
          <p className="mt-4 text-sm text-muted-foreground" data-testid="home-hero-term-note">
            Booking is open now &mdash; {TERM.label.toLowerCase()} runs {TERM.start} to {TERM.end}.
          </p>
        </FadeUp>
        <FadeUp delay={0.24}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Badge
              data-testid="home-hero-istd-record-badge"
              className="rounded-none bg-[hsl(var(--kerrys-sun))] text-foreground border-[0.5px] border-foreground font-display uppercase tracking-[0.08em] text-sm px-4 py-1.5 hover:bg-[hsl(var(--kerrys-sun))]"
            >
              100% ISTD exam pass record
            </Badge>
            <p className="text-sm text-muted-foreground">Every dancer we&rsquo;ve entered has passed. Every single one.</p>
          </div>
        </FadeUp>
      </div>
      <div className="lg:col-span-5 relative lg:-mr-10">
        <FadeUp delay={0.1}>
          <div className="relative">
            <img
              src={HERO_IMG}
              alt="Dancer mid-jump, full of energy"
              className="w-full h-[380px] sm:h-[480px] lg:h-[600px] object-cover border-[0.5px] border-foreground"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
            <div
              className="absolute -bottom-7 -left-4 sm:-left-8 bg-foreground text-background px-6 py-5 -rotate-2 border-[0.5px] border-background/20"
              data-testid="home-hero-stat-pill"
            >
              <p className="font-display text-5xl leading-none">100%</p>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] mt-1.5 text-background/80">ISTD pass record</p>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const MarqueeBand = () => {
  const items = ["Ballet", "Tap", "Modern", "Musical Theatre", "Street", "Contemporary", "Singing", "Happy Feet", "Audition Prep", "Body Conditioning"];
  const row = [...items, ...items, ...items];
  const colors = ["text-foreground", "text-[hsl(var(--kerrys-coral))]", "text-[hsl(var(--kerrys-sea))]"];
  return (
    <div className="bg-[hsl(var(--kerrys-sun))] border-b-[0.5px] border-foreground/60 overflow-hidden py-4 grain" data-testid="home-marquee-band">
      <div className="marquee-track flex w-max items-center gap-10 pr-10">
        {row.map((label, i) => (
          <span key={i} className="flex items-center gap-10 shrink-0">
            <span className={`font-display uppercase text-4xl sm:text-5xl leading-none ${colors[i % 3]}`}>{label}</span>
            <Star className="h-4 w-4 text-foreground" fill="currentColor" />
          </span>
        ))}
      </div>
    </div>
  );
};

const Stats = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]" data-testid="home-stats-section">
    <SectionHeading eyebrow="The bragging bit" title="Numbers we're proud of" sub="We don't shout about much. These, we will." />
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 mt-16">
      <FadeUp className="sm:col-span-5">
        <div className="border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] p-8 sm:p-10 h-full grain" data-testid="home-stats-tile">
          <p className="font-display text-7xl sm:text-8xl leading-none" data-testid="home-stats-value">
            <CountUp to={100} suffix="%" testId="home-stats-value-istd" />
          </p>
          <p className="text-sm font-bold uppercase tracking-[0.2em] mt-4">ISTD exam pass record</p>
          <p className="text-sm text-foreground/70 mt-2 max-w-xs">Ballet, tap and modern &mdash; every entry, every grade, passed.</p>
        </div>
      </FadeUp>
      <FadeUp delay={0.08} className="sm:col-span-4 lg:translate-y-8">
        <div className="border-[0.5px] border-foreground bg-card p-8 sm:p-10 h-full" data-testid="home-stats-tile">
          <p className="font-display text-7xl sm:text-8xl leading-none" data-testid="home-stats-value">
            2&ndash;18
          </p>
          <p className="text-sm font-bold uppercase tracking-[0.2em] mt-4">Ages welcome</p>
          <p className="text-sm text-muted-foreground mt-2">From first wobbly pli&eacute;s to advanced contemporary.</p>
        </div>
      </FadeUp>
      <FadeUp delay={0.16} className="sm:col-span-3 lg:translate-y-16">
        <div className="border-[0.5px] border-foreground bg-card p-8 sm:p-10 h-full" data-testid="home-stats-tile">
          <p className="font-display text-7xl sm:text-8xl leading-none" data-testid="home-stats-value">
            <CountUp to={17} testId="home-stats-value-years" />
          </p>
          <p className="text-sm font-bold uppercase tracking-[0.2em] mt-4">Years of teaching</p>
          <p className="text-sm text-muted-foreground mt-2">Kerry has trained Whitstable&rsquo;s dancers for 17 years and counting.</p>
        </div>
      </FadeUp>
      <FadeUp delay={0.2} className="sm:col-span-12 lg:mt-16">
        <div className="border-[0.5px] border-foreground bg-card px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="home-stats-tile">
          <p className="font-display text-3xl sm:text-4xl uppercase leading-none">
            <span data-testid="home-stats-value"><CountUp to={4} testId="home-stats-value-venues" /></span> studios, one seaside town
          </p>
          <Link to="/locations" className="text-sm font-bold uppercase tracking-[0.15em] text-[hsl(var(--kerrys-sea))] hover:text-foreground transition-colors inline-flex items-center gap-2" data-testid="home-stats-locations-link">
            Umbrella &middot; Whitstable School &middot; Pool Studio &middot; Treetops <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </FadeUp>
    </div>
  </section>
);

const TodayAtKerrys = () => {
  const [classes, setClasses] = useState(null);
  useEffect(() => {
    api
      .get("/timetable")
      .then((res) => setClasses(res.data))
      .catch(() => setClasses([]));
  }, []);

  const todayName = DAYS[new Date().getDay()];
  let label = `Today · ${todayName}`;
  let list = (classes || []).filter((c) => c.day === todayName);
  if (classes && list.length === 0) {
    const order = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const todayIdx = order.indexOf(todayName);
    for (let i = 1; i <= 7; i++) {
      const next = order[(todayIdx + i) % 7];
      const nextList = classes.filter((c) => c.day === next);
      if (nextList.length > 0) {
        label = `Next up · ${next}`;
        list = nextList;
        break;
      }
    }
  }

  return (
    <section className="bg-secondary border-y-[0.5px] border-foreground/40 grain" data-testid="home-today-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <SectionHeading eyebrow="Straight from the timetable" title="Dancing this week" sub={label} />
          <Button
            asChild
            variant="outline"
            className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs w-fit h-11 px-6"
            data-testid="home-today-full-timetable-button"
          >
            <Link to="/timetable">
              Full timetable <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
        {!classes ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-none" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <p className="text-muted-foreground">The timetable&rsquo;s being updated &mdash; check back soon.</p>
        ) : (
          <div className="border-t-[0.5px] border-foreground/50">
            {list.slice(0, 6).map((c, i) => {
              const meta = STYLE_META[c.style] || {};
              return (
                <FadeUp key={c.id} delay={i * 0.04}>
                  <div
                    className="grid grid-cols-12 items-center gap-3 py-5 border-b-[0.5px] border-foreground/50 hover:bg-background/60 transition-colors px-2"
                    data-testid="home-today-class-card"
                  >
                    <p className="font-display text-2xl sm:text-3xl leading-none col-span-4 sm:col-span-2 tabular-nums">
                      {fmtTime(c.start_time)}
                    </p>
                    <div className="col-span-8 sm:col-span-6 min-w-0">
                      <p className="font-bold truncate">{c.name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {c.age_group} &middot; {c.venue}
                      </p>
                    </div>
                    <div className="col-span-12 sm:col-span-4 flex sm:justify-end">
                      <Badge variant="outline" className={`rounded-none border-[0.5px] font-bold text-xs ${meta.badge || ""}`}>
                        {c.style}
                      </Badge>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

const ClassIndex = () => {
  const styles = Object.entries(STYLE_META).filter(([name]) => !["Early Years", "Body Conditioning"].includes(name));
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]" data-testid="home-classes-section">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <SectionHeading
          eyebrow="Pick your thing"
          title="Eight styles, zero pressure"
          sub="No auditions to join — come try the one that looks fun. Change your mind later &mdash; they all count."
        />
        <Button
          asChild
          variant="outline"
          className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs w-fit h-11 px-6"
          data-testid="home-classes-all-button"
        >
          <Link to="/classes">
            All classes <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </Button>
      </div>
      <div className="border-t-[0.5px] border-foreground">
        {styles.map(([name, meta], i) => (
          <FadeUp key={name} delay={i * 0.03}>
            <Link
              to="/classes"
              className="group grid grid-cols-12 items-center gap-4 py-7 sm:py-9 border-b-[0.5px] border-foreground px-2 sm:px-4 hover:bg-foreground transition-colors"
              data-testid="classes-style-card"
            >
              <span className="font-display text-xl sm:text-2xl text-muted-foreground group-hover:text-background/50 col-span-2 sm:col-span-1 transition-colors">
                0{i + 1}
              </span>
              <span className="font-display uppercase text-4xl sm:text-6xl leading-[0.9] col-span-10 sm:col-span-5 text-foreground group-hover:text-background transition-colors">
                {name}
              </span>
              <span className="hidden sm:block col-span-4 text-sm text-muted-foreground group-hover:text-background/70 leading-snug transition-colors">
                {meta.blurb}
              </span>
              <span className="hidden sm:flex col-span-2 items-center justify-end gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground group-hover:text-[hsl(var(--kerrys-sun))] transition-colors">
                  {meta.ages}
                </span>
                <ArrowUpRight className="h-5 w-5 text-foreground group-hover:text-background group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </span>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const RULES = [
  { n: "01", title: "Everyone's in", text: "No auditions, no 'dance school scary'. Every child is welcome here — including SEND and neurodiverse dancers. Tell us what helps your child feel at home, and that's exactly what we'll do." },
  { n: "02", title: "Fun comes first", text: "Kids learn fastest when they're grinning. Stand outside the studio door for a minute — you'll hear it." },
  { n: "03", title: "Proper training", text: "ISTD syllabus, patient teaching, and a 100% exam pass record. The real thing, not a certificate factory." },
  { n: "04", title: "Everyone performs", text: "When show time comes around, every single dancer gets their moment on stage. Not just the front row." },
];

const Manifesto = () => (
  <section className="bg-[hsl(var(--kerrys-ink))] text-background grain" data-testid="home-values-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
      <SectionHeading light eyebrow="Our promise" title="The house rules" sub="Four things we promise every family who walks through the door." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14 mt-20">
        {RULES.map((rule, i) => (
          <FadeUp key={rule.n} delay={i * 0.06} className={i % 2 === 1 ? "md:translate-y-14" : ""}>
            <div className="border-t-[0.5px] border-background/40 pt-6" data-testid="home-value-card">
              <p className="font-display text-2xl text-[hsl(var(--kerrys-coral))]">{rule.n}</p>
              <h3 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92] mt-3">{rule.title}</h3>
              <p className="text-background/70 mt-4 leading-relaxed max-w-md">{rule.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const LocationsTeaser = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]" data-testid="home-locations-section">
    <SectionHeading
      eyebrow="Where we dance"
      title="Three studios by the sea"
      sub="Weekly classes run at three Whitstable venues — The Umbrella Centre, The Whitstable School and the Pool dance studio — with one-to-one private lessons at a fourth. All minutes from the sea."
    />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-16">
      <FadeUp className="lg:col-span-7">
        <div className="border-[0.5px] border-foreground bg-card h-full flex flex-col" data-testid="locations-location-card">
          <img src={SEASIDE_IMG} alt="Whitstable seafront beach huts" loading="lazy" className="w-full h-44 sm:h-56 object-cover border-b-[0.5px] border-foreground" />
          <div className="p-8 sm:p-10 flex-1 flex flex-col">
            <h3 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92]">{VENUES[0].name}</h3>
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground mt-3">{VENUES[0].short}</p>
            <p className="text-muted-foreground mt-4 leading-relaxed max-w-md">{VENUES[0].blurb}</p>
            <div className="mt-auto pt-8">
              <Button
                asChild
                variant="outline"
                className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
              >
                <Link to="/locations" data-testid="home-location-more-button">
                  Venue details <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-5 lg:mt-20">
        <div className="border-[0.5px] border-foreground bg-secondary grain h-full p-8 sm:p-10 flex flex-col" data-testid="locations-location-card">
          <h3 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92]">{VENUES[1].name}</h3>
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground mt-3">{VENUES[1].short}</p>
          <p className="text-muted-foreground mt-4 leading-relaxed">{VENUES[1].blurb}</p>
          <div className="mt-auto pt-8">
            <Button
              asChild
              variant="outline"
              className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
            >
              <Link to="/locations" data-testid="home-location-more-button">
                Venue details <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </FadeUp>
      <FadeUp delay={0.14} className="lg:col-span-12">
        <div className="border-[0.5px] border-foreground bg-card px-8 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="locations-location-card">
          <div>
            <h3 className="font-display uppercase text-4xl sm:text-5xl leading-[0.92]">{VENUES[2].name}</h3>
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-muted-foreground mt-3">{VENUES[2].short} &middot; {VENUES[2].tag}</p>
            <p className="text-muted-foreground mt-4 leading-relaxed max-w-2xl">{VENUES[2].blurb}</p>
          </div>
          <Button
            asChild
            variant="outline"
            className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs h-11 px-6 shrink-0"
          >
            <Link to="/locations" data-testid="home-location-more-button">
              Venue details <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
      </FadeUp>
    </div>
  </section>
);

const BlogTeaser = () => {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    api
      .get("/blog")
      .then((res) => setPosts(res.data.filter((p) => p.category !== "Past Students").slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);
  if (posts && posts.length === 0) return null;
  const [featured, ...rest] = posts || [];
  return (
    <section className="border-t-[0.5px] border-foreground/40" data-testid="home-blog-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <SectionHeading eyebrow="From the studio" title="Fresh off the noticeboard" />
          <Button
            asChild
            variant="outline"
            className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-xs w-fit h-11 px-6"
            data-testid="home-blog-all-button"
          >
            <Link to="/blog">
              All posts <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
        {!posts ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-96 rounded-none" />
            <Skeleton className="h-96 rounded-none" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {featured && (
              <FadeUp className="lg:col-span-7">
                <Link to={`/blog/${featured.slug}`} className="group block border-[0.5px] border-foreground bg-card h-full" data-testid="home-blog-post-card">
                  <div className="overflow-hidden border-b-[0.5px] border-foreground">
                    <img
                      src={featured.image_url || FALLBACK_IMAGE}
                      alt={featured.title}
                      loading="lazy"
                      className="w-full h-64 sm:h-80 object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="p-8">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))]">
                      {featured.category} &middot; {fmtDate(featured.created_at)}
                    </p>
                    <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95] mt-4 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors">
                      {featured.title}
                    </h3>
                    <p className="text-muted-foreground mt-4 line-clamp-2">{featured.excerpt}</p>
                  </div>
                </Link>
              </FadeUp>
            )}
            <div className="lg:col-span-5 flex flex-col gap-6 lg:mt-16">
              {rest.map((p, i) => (
                <FadeUp key={p.id} delay={0.08 + i * 0.06}>
                  <Link to={`/blog/${p.slug}`} className="group block border-[0.5px] border-foreground bg-card p-7" data-testid="home-blog-post-card">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--kerrys-sea))]">
                      {p.category} &middot; {fmtDate(p.created_at)}
                    </p>
                    <h3 className="font-display uppercase text-2xl sm:text-3xl leading-[0.95] mt-3 group-hover:text-[hsl(var(--kerrys-coral))] transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.excerpt}</p>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const CtaBand = () => (
  <section className="bg-[hsl(var(--kerrys-coral))] border-t-[0.5px] border-foreground grain" data-testid="home-cta-section">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[120px]">
      <FadeUp>
        <h2 className="font-display uppercase text-6xl sm:text-8xl lg:text-9xl leading-[0.88] text-foreground">
          Come and
          <br />
          dance with us.
        </h2>
        <p className="mt-8 text-foreground/80 text-lg max-w-xl leading-relaxed">
          First class is a no-pressure taster. Book online in a couple of minutes &mdash; or just give Kerry a ring and
          have a chat. Booking is open now, and classes start with the new term on {TERM.start}.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Button
            asChild
            size="lg"
            data-testid="home-cta-book-button"
            className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
          >
            <Link to="/book">
              Book a class <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            data-testid="home-cta-timetable-button"
            className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
          >
            <Link to="/timetable">See the timetable</Link>
          </Button>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo path="/" />
      <Hero />
      <MarqueeBand />
      <Stats />
      <ClassIndex />
      <TodayAtKerrys />
      <Manifesto />
      <ReviewsWall withJsonLd variant="light" />
      <LocationsTeaser />
      <BlogTeaser />
      <CtaBand />
    </>
  );
}
